import type {
  ShopStatusDTO,
  PublicBarberDTO,
  PublicQueueDTO,
  LookupResponseDTO,
  RegisterResponseDTO,
  VisitStatusDTO,
  StaffQueueDTO,
  AdminCustomerListItemDTO,
  AdminCustomerDetailDTO,
  AdminAnalyticsDTO,
  SettingsDTO,
  BarberLiveStatus,
} from '@nine80/shared';

/**
 * Thin typed fetch wrapper. Every mutation is server-validated regardless of what the
 * client sent — this file's only job is shaping requests/responses, never deciding
 * whether an action is allowed.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  token?: string;
  constructor(message: string, status: number, code?: string, token?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.token = token;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
      credentials: 'include',
    });
  } catch {
    throw new ApiError('network_error', 0, 'network_error');
  }

  let body: Record<string, unknown> | null = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    throw new ApiError(
      (body?.message as string) ?? 'Request failed',
      res.status,
      body?.error as string | undefined,
      body?.token as string | undefined,
    );
  }
  return body as T;
}

export interface RegisterPayload {
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  requestedBarberId: number | null;
  marketingConsent: boolean;
  source: 'WEB' | 'KIOSK';
  language: 'en' | 'es';
  website?: string;
}

export interface RegisterReturningPayload {
  phone: string;
  requestedBarberId: number | null;
  source: 'WEB' | 'KIOSK';
  language: 'en' | 'es';
  website?: string;
  updatedFirstName?: string;
  updatedLastName?: string;
  updatedEmail?: string;
}

export const api = {
  shopStatus: () => request<ShopStatusDTO>('/shop-status'),
  barbers: () => request<{ barbers: PublicBarberDTO[] }>('/barbers'),
  queue: () => request<PublicQueueDTO>('/queue'),
  consentText: () => request<{ en: string; es: string }>('/consent-text'),
  booksyUrl: () => request<{ url: string | null }>('/booksy-url'),
  kioskConfig: () =>
    request<{ inactivityTimeoutSeconds: number; confirmationSeconds: number }>('/kiosk-config'),
  lookup: (phone: string) =>
    request<LookupResponseDTO>('/lookup', { method: 'POST', body: JSON.stringify({ phone }) }),
  register: (payload: RegisterPayload) =>
    request<RegisterResponseDTO>('/visits', { method: 'POST', body: JSON.stringify(payload) }),
  registerReturning: (payload: RegisterReturningPayload) =>
    request<RegisterResponseDTO>('/visits/returning', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  visitStatus: (token: string) =>
    request<VisitStatusDTO>(`/visits/status/${encodeURIComponent(token)}`),
  arrive: (token: string) =>
    request<{ ok: true }>(`/visits/${encodeURIComponent(token)}/arrive`, { method: 'POST' }),
  cancelVisit: (token: string) =>
    request<{ ok: true }>(`/visits/${encodeURIComponent(token)}/cancel`, { method: 'POST' }),

  staffLogin: (pin: string) =>
    request<{ ok: true }>('/staff/login', { method: 'POST', body: JSON.stringify({ pin }) }),
  staffLogout: () => request<{ ok: true }>('/staff/logout', { method: 'POST' }),
  staffMe: () => request<{ role: 'staff' | 'admin' }>('/staff/me'),
  staffQueue: () => request<StaffQueueDTO>('/staff/queue'),
  staffPresent: (id: number) =>
    request<{ ok: true }>(`/staff/visits/${id}/present`, { method: 'POST' }),
  staffTake: (id: number, barberId: number) =>
    request<{ ok: true }>(`/staff/visits/${id}/take`, {
      method: 'POST',
      body: JSON.stringify({ barberId }),
    }),
  staffComplete: (id: number) =>
    request<{ ok: true }>(`/staff/visits/${id}/complete`, { method: 'POST' }),
  staffNoShow: (id: number) =>
    request<{ ok: true }>(`/staff/visits/${id}/no-show`, { method: 'POST' }),
  staffCancel: (id: number) =>
    request<{ ok: true }>(`/staff/visits/${id}/cancel`, { method: 'POST' }),
  staffReassign: (id: number, requestedBarberId: number | null) =>
    request<{ ok: true }>(`/staff/visits/${id}/reassign`, {
      method: 'PATCH',
      body: JSON.stringify({ requestedBarberId }),
    }),
  staffBarberStatus: (id: number, liveStatus: BarberLiveStatus) =>
    request<{ ok: true }>(`/staff/barbers/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ liveStatus }),
    }),

  adminCustomers: (search: string, page: number) =>
    request<{ items: AdminCustomerListItemDTO[]; total: number; page: number; pageSize: number }>(
      `/admin/customers?search=${encodeURIComponent(search)}&page=${page}`,
    ),
  adminCustomer: (id: number) => request<AdminCustomerDetailDTO>(`/admin/customers/${id}`),
  adminUpdateCustomer: (id: number, patch: Record<string, unknown>) =>
    request<{ ok: true }>(`/admin/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  adminAnalytics: (date: string) => request<AdminAnalyticsDTO>(`/admin/analytics?date=${date}`),
  adminBarbers: () =>
    request<{
      barbers: Array<{
        id: number;
        slug: string;
        displayName: string;
        active: boolean;
        displayOrder: number;
        liveStatus: BarberLiveStatus;
      }>;
    }>('/admin/barbers'),
  adminUpdateBarber: (id: number, patch: Record<string, unknown>) =>
    request<{ ok: true }>(`/admin/barbers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  adminBarberSchedule: (id: number) =>
    request<{
      weekly: Array<{
        weekday: number;
        enabled: boolean;
        startMinutes: number | null;
        endMinutes: number | null;
      }>;
      overrides: Array<{
        date: string;
        enabled: boolean;
        startMinutes: number | null;
        endMinutes: number | null;
        notes: string | null;
      }>;
    }>(`/admin/barbers/${id}/schedule`),
  adminSetBarberSchedule: (id: number, weekly: unknown) =>
    request<{ ok: true }>(`/admin/barbers/${id}/schedule`, {
      method: 'PUT',
      body: JSON.stringify(weekly),
    }),
  adminSetBarberOverride: (id: number, override: unknown) =>
    request<{ ok: true }>(`/admin/barbers/${id}/schedule-overrides`, {
      method: 'POST',
      body: JSON.stringify(override),
    }),
  adminRemoveBarberOverride: (id: number, date: string) =>
    request<{ ok: true }>(`/admin/barbers/${id}/schedule-overrides/${date}`, { method: 'DELETE' }),
  adminHours: () =>
    request<{
      weekly: Array<{
        weekday: number;
        open: boolean;
        startMinutes: number | null;
        endMinutes: number | null;
      }>;
      overrides: Array<{
        date: string;
        open: boolean;
        startMinutes: number | null;
        endMinutes: number | null;
        queueAcceptanceOverride: boolean | null;
        notes: string | null;
      }>;
    }>('/admin/hours'),
  adminSetHours: (weekly: unknown) =>
    request<{ ok: true }>('/admin/hours', { method: 'PUT', body: JSON.stringify(weekly) }),
  adminSetHoursOverride: (override: unknown) =>
    request<{ ok: true }>('/admin/hours/overrides', {
      method: 'POST',
      body: JSON.stringify(override),
    }),
  adminRemoveHoursOverride: (date: string) =>
    request<{ ok: true }>(`/admin/hours/overrides/${date}`, { method: 'DELETE' }),
  adminSettings: () => request<SettingsDTO>('/admin/settings'),
  adminUpdateSettings: (patch: Record<string, unknown>) =>
    request<{ ok: true }>('/admin/settings', { method: 'PATCH', body: JSON.stringify(patch) }),
  adminQueueControl: (queueEnabled: boolean, message: string | null) =>
    request<{ ok: true }>('/admin/queue-control', {
      method: 'POST',
      body: JSON.stringify({ queueEnabled, message }),
    }),
  adminManualVisit: (payload: Record<string, unknown>) =>
    request<{ ok: true; token: string }>('/admin/visits', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  adminSystemStatus: () =>
    request<{
      apiOnline: boolean;
      databaseAvailable: boolean;
      emailConfigured: boolean;
      pinConfigured: boolean;
      booksyConfigured: boolean;
      shopOpen: boolean;
      acceptingWalkins: boolean;
    }>('/admin/system-status'),
  adminExportMarketingUrl: (all: boolean) =>
    `/api/admin/export/marketing.csv${all ? '?all=true' : ''}`,
  adminExportOperationalUrl: () => `/api/admin/export/operational.csv`,
};
